import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Target, Megaphone, TrendingUp, Instagram } from 'lucide-react';
import { stats } from '../../data/mock';
import { useAuth } from '../../contexts/AuthContext';

const niches = [
  "Faceless Theme Page Owners",
  "Dropshippers & E-commerce Sellers",
  "Coaches & Consultants",
  "Personal Brand Builders",
  "Influencer Pages",
  "Niche Page Builders",
  "Social Media Agencies",
  "Content Creators Who Hate Editing",
  "Email Marketers",
  "SaaS Founders & App Builders",
  "Real Estate Content Pages",
  "Crypto & Web3 Community Pages",
  "Fitness Pages & Online Trainers",
  "Side Hustlers & Beginners"
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/j0h273d6_auto.png";

const HeroSection = () => {
  const [currentNicheIndex, setCurrentNicheIndex] = useState(0);
  const [isBeeping, setIsBeeping] = useState(false);
  const { isAuthenticated, setShowAuthModal } = useAuth();

  const handleGetAccess = (e) => {
    e.preventDefault();
    if (isAuthenticated) {
      window.location.href = '/dashboard';
    } else {
      setShowAuthModal('signup');
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // Trigger beep animation
      setIsBeeping(true);
      
      // Change niche after a small delay (synced with beep)
      setTimeout(() => {
        setCurrentNicheIndex((prev) => (prev + 1) % niches.length);
      }, 100);

      // Reset beep state
      setTimeout(() => {
        setIsBeeping(false);
      }, 400);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const valuePoints = [
    { icon: Target, text: "Driving traffic" },
    { icon: Megaphone, text: "Promoting offers" },
    { icon: TrendingUp, text: "Scaling income" }
  ];

  return (
    <section 
      className="min-h-screen flex items-center justify-center relative pt-28 pb-16 px-6"
      style={{
        background: `radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.03) 0%, transparent 50%),
                     radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.02) 0%, transparent 40%),
                     #0a0a0a`
      }}
    >
      {/* CSS for animations */}
      <style>{`
        @keyframes beep {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.7);
          }
          50% {
            transform: scale(1.8);
            box-shadow: 0 0 0 10px rgba(52, 211, 153, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(52, 211, 153, 0.5), 0 0 10px rgba(52, 211, 153, 0.3);
          }
          50% {
            box-shadow: 0 0 15px rgba(52, 211, 153, 0.8), 0 0 25px rgba(52, 211, 153, 0.5);
          }
        }
        
        .beep-dot {
          animation: glow 2s ease-in-out infinite;
        }
        
        .beep-dot.beeping {
          animation: beep 0.4s ease-out;
        }
        
        @keyframes fadeSlide {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .niche-text {
          animation: fadeSlide 0.3s ease-out;
        }
        
        @keyframes logoPulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
          }
          50% {
            transform: scale(1.05);
            filter: drop-shadow(0 0 25px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 50px rgba(52, 211, 153, 0.4));
          }
        }
        
        @keyframes logoEntrance {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(20px);
          }
          50% {
            opacity: 1;
            transform: scale(1.1) translateY(-5px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .hero-logo {
          animation: logoEntrance 1s ease-out, logoPulse 3s ease-in-out 1s infinite;
        }
      `}</style>

      <div className="max-w-5xl mx-auto text-center">
        {/* Hero Logo - Centered above niche badge */}
        <div className="mb-8">
          <img 
            src={LOGO_URL} 
            alt="AutoPostr" 
            className="hero-logo h-48 md:h-60 w-auto mx-auto"
          />
        </div>

        {/* Post to All Platforms - Phone Mockups */}
        <div className="mb-10">
          <div className="flex justify-center items-end gap-1 sm:gap-2 md:gap-3 lg:gap-4 mb-6 overflow-hidden px-2">
            {/* YouTube */}
            <div className="w-14 sm:w-20 md:w-24 lg:w-32 transform -rotate-6 translate-y-3 sm:translate-y-4">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-red-700/30 to-red-900/20 flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-red-500 sm:w-7 sm:h-7 md:w-8 md:h-8">
                        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-1.5 bg-gray-900/80"><p className="text-white text-[6px] sm:text-[7px] md:text-[8px] font-medium truncate text-center">YouTube</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Instagram */}
            <div className="w-16 sm:w-24 md:w-28 lg:w-36 transform -rotate-3 translate-y-1.5 sm:translate-y-2 z-10">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-orange-500/20 flex items-center justify-center">
                      <Instagram size={28} className="text-pink-500 sm:w-8 sm:h-8 md:w-9 md:h-9" />
                    </div>
                    <div className="p-1 sm:p-1.5 bg-gray-900/80"><p className="text-white text-[6px] sm:text-[7px] md:text-[8px] font-medium truncate text-center">Instagram</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* TikTok - Center/Largest */}
            <div className="w-20 sm:w-28 md:w-32 lg:w-40 z-20">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-cyan-500/20 to-pink-500/20 flex items-center justify-center">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" className="text-white sm:w-9 sm:h-9 md:w-10 md:h-10">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-1.5 bg-gray-900/80"><p className="text-white text-[6px] sm:text-[7px] md:text-[8px] font-medium truncate text-center">TikTok</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Facebook */}
            <div className="w-16 sm:w-24 md:w-28 lg:w-36 transform rotate-3 translate-y-1.5 sm:translate-y-2 z-10">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-blue-600/20 to-blue-900/20 flex items-center justify-center">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 sm:w-8 sm:h-8 md:w-9 md:h-9">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-1.5 bg-gray-900/80"><p className="text-white text-[6px] sm:text-[7px] md:text-[8px] font-medium truncate text-center">Facebook</p></div>
                  </div>
                </div>
              </div>
            </div>
            {/* X (Twitter) */}
            <div className="w-14 sm:w-20 md:w-24 lg:w-32 transform rotate-6 translate-y-3 sm:translate-y-4">
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-0.5 sm:p-1 shadow-2xl border border-white/10">
                <div className="bg-black rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden aspect-[9/16]">
                  <div className="h-full flex flex-col">
                    <div className="flex-1 bg-gradient-to-br from-gray-700/20 to-gray-900/30 flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-white sm:w-7 sm:h-7 md:w-8 md:h-8">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div className="p-1 sm:p-1.5 bg-gray-900/80"><p className="text-white text-[6px] sm:text-[7px] md:text-[8px] font-medium truncate text-center">X</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">Post to All Platforms at ONCE</h2>
          <p className="text-white/60 mb-4">in One Click 🔥</p>
          <button onClick={handleGetAccess} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors">
            🚀 Get Access Now
          </button>
        </div>

        {/* Animated Niche Badge */}
        <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 mb-8 min-w-[320px]">
          <div 
            className={`w-3 h-3 rounded-full bg-emerald-400 beep-dot ${isBeeping ? 'beeping' : ''}`}
          />
          <span className="text-sm font-medium text-white/90">
            For{' '}
            <span 
              key={currentNicheIndex} 
              className="niche-text inline-block text-emerald-400"
            >
              {niches[currentNicheIndex]}
            </span>
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="heading-1 mb-6 max-w-4xl mx-auto text-white">
          Monthly Social Media Posting.
          <br />
          <span className="text-white/60">Done For You. Automated.</span>
        </h1>

        {/* Subheadline */}
        <p className="body-large text-[#a3a3a3] max-w-3xl mx-auto mb-6">
          Post daily across TikTok, Instagram, YouTube, X, Threads, LinkedIn, Facebook and many more — using faceless, branded, or custom content — without lifting a finger.
        </p>

        {/* Agency description */}
        <p className="text-base text-white/50 max-w-2xl mx-auto mb-10">
          We are a Social Media Agency dedicated to solving every social media posting problem— so you can grow without the stress. Built for digital marketers, affiliate marketers, creators, and online businesses, we help you scale your pages, promote your offers, and generate consistent income—without the overwhelm.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button onClick={handleGetAccess} data-testid="hero-start-posting-btn" className="btn-primary text-base px-8 py-4">
            Start Posting Today
            <ArrowRight size={18} />
          </button>
          <a href="/#how-it-works" className="btn-secondary text-base px-8 py-4">
            <Play size={18} />
            See How It Works
          </a>
        </div>

        {/* Value Points */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 max-w-2xl mx-auto mb-12">
          <p className="text-white/60 text-sm mb-4">
            We help you schedule and post up to a full month of content automatically so you can focus on:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {valuePoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5">
                <point.icon size={16} className="text-emerald-400" />
                <span className="text-white font-medium">{point.text}</span>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-sm mt-4">Instead of worrying about what to post every day.</p>
        </div>

        {/* Stats - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {stats.map((stat) => (
            <div 
              key={stat.id} 
              className="bg-[#141414] border border-white/10 rounded-2xl p-6 text-center hover:border-white/20 transition-all duration-300"
            >
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-[#737373]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative gradient at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none"></div>
    </section>
  );
};

export default HeroSection;
